import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Table, Tag, Empty, Space, Tooltip, Typography, message } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import dayjs from 'dayjs';
import { operationLogService, type OperationLogItem } from '../services/operationLog';

const { Text, Paragraph } = Typography;

const DEFAULT_PAGE_SIZE = 20;

const formatNullable = (value?: string) => {
  if (!value) {
    return '-';
  }
  return value;
};

const renderLongText = (value?: string) => {
  if (!value) {
    return '-';
  }
  const display = value.length > 120 ? `${value.slice(0, 120)}...` : value;
  return (
    <Tooltip placement="topLeft" title={<Paragraph style={{ maxWidth: 480 }}>{value}</Paragraph>}>
      <Text>{display}</Text>
    </Tooltip>
  );
};

const OperationLogs: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<OperationLogItem[]>([]);
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    total: 0,
    showSizeChanger: true,
    pageSizeOptions: ['10', '20', '50', '100'],
  });

  const columns: ColumnsType<OperationLogItem> = useMemo(
    () => [
      {
        title: '操作时间',
        dataIndex: 'created_at',
        key: 'created_at',
        width: 190,
        render: (value: string) => dayjs(value).format('YYYY-MM-DD HH:mm:ss'),
      },
      {
        title: '操作人',
        dataIndex: 'username',
        key: 'username',
        width: 160,
        render: (value: string, record) => (
          <Space direction="vertical" size={2}>
            <Text strong>{value || '-'}</Text>
            <Text type="secondary">{record.user_type || '-'}</Text>
          </Space>
        ),
      },
      {
        title: '动作 / 资源',
        dataIndex: 'action',
        key: 'action_resource',
        width: 200,
        render: (_: any, record) => (
          <Space direction="vertical" size={2}>
            <Text>{record.action || '-'}</Text>
            <Text type="secondary">{formatNullable(record.resource)}</Text>
          </Space>
        ),
      },
      {
        title: '请求信息',
        dataIndex: 'method',
        key: 'request_info',
        width: 240,
        render: (_: any, record) => (
          <Space direction="vertical" size={2}>
            <Text>
              <Tag color="blue" style={{ marginRight: 8 }}>{record.method}</Tag>
              {record.path}
            </Text>
            <Text type="secondary">RequestID: {formatNullable(record.request_id)}</Text>
          </Space>
        ),
      },
      {
        title: '结果',
        dataIndex: 'success',
        key: 'success',
        width: 130,
        render: (success: boolean, record) => (
          <Space direction="vertical" size={2}>
            <Tag color={success ? 'success' : 'error'}>{success ? '成功' : '失败'}</Tag>
            <Text type="secondary">状态码: {record.status_code}</Text>
            <Text type="secondary">耗时: {record.execution_time}ms</Text>
          </Space>
        ),
      },
      {
        title: 'IP / UA',
        dataIndex: 'ip_address',
        key: 'ip_address',
        width: 220,
        render: (_: any, record) => (
          <Space direction="vertical" size={2}>
            <Text>{formatNullable(record.ip_address)}</Text>
            {renderLongText(record.user_agent)}
          </Space>
        ),
      },
      {
        title: '消息',
        dataIndex: 'message',
        key: 'message',
        width: 220,
        render: (value?: string) => renderLongText(value),
      },
      {
        title: '请求体',
        dataIndex: 'request_body',
        key: 'request_body',
        width: 240,
        render: (value?: string) => renderLongText(value),
      },
      {
        title: '响应体',
        dataIndex: 'response_body',
        key: 'response_body',
        width: 240,
        render: (value?: string) => renderLongText(value),
      },
    ],
    [],
  );

  const fetchData = useCallback(async (page = 1, pageSize = DEFAULT_PAGE_SIZE) => {
    setLoading(true);
    try {
      const response = await operationLogService.list({ page, size: pageSize });
      setData(response.list || []);
      setPagination((prev) => ({
        ...prev,
        current: response.page || page,
        pageSize: response.size || pageSize,
        total: response.total || 0,
      }));
    } catch (error) {
      console.error('加载操作日志失败', error);
      message.error('加载操作日志失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(1, DEFAULT_PAGE_SIZE);
  }, [fetchData]);

  const handleTableChange = (pager: TablePaginationConfig) => {
    const { current = 1, pageSize = DEFAULT_PAGE_SIZE } = pager;
    fetchData(current, pageSize);
  };

  return (
    <Card title="操作记录" bordered={false}>
      <Table<OperationLogItem>
        rowKey={(record) => `${record.id}`}
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
        scroll={{ x: 1400 }}
        locale={{ emptyText: <Empty description="暂无操作记录" /> }}
      />
    </Card>
  );
};

export default OperationLogs;


